import React from 'react';

/**
 * The extracted document text, rendered as real HTML on the document's own
 * page.
 *
 * This is the archive's unique asset and until now it was invisible: the text
 * lives in Solr as `file_text`, so our own search can quote a snippet of it,
 * but the document page itself was a metadata stub with a download link and a
 * crawler had no way to reach the words inside the PDF.
 *
 * Rendered from `content.full_text_sections`, which the backend ships with the
 * ordinary content GET — the same payload DocumentView already receives. There
 * is no fetch here on purpose: a client-side load would put the text outside
 * the server-rendered HTML, which is the one place it needs to be.
 *
 * Sections are `section-N`, not `page-N`. Tika's plain-text output carries no
 * page breaks at all (measured over the live index), so page anchors are not
 * derivable — see backend `dox/policyportal/core/fulltext.py`.
 *
 * Renders nothing when the field is absent or empty, so this is safe on every
 * document that has not been through the reconciler.
 *
 * Rendered as a native `<details>`, collapsed by default (BL-0020, same
 * pattern as VersionRelations/BL-0015): the text is on the page so a crawler
 * can reach the words inside the PDF, not for a visitor to read a wall of
 * raw extraction below the metadata. `<details>` content is present in the
 * DOM either way -- unlike `display:none`, Google credits it the same as
 * visible text -- so collapsing it changes nothing a crawler sees. The
 * `section-N` anchors still work as deep links when collapsed: browsers
 * auto-expand a `<details>` whose content is the target of a URL fragment.
 */

const FullTextSection = ({ content }) => {
  const sections = content?.full_text_sections;
  if (!Array.isArray(sections) || sections.length === 0) return null;

  return (
    <details className="prc-kit__fulltext doc-fulltext" aria-label="Document text">
      <summary className="doc-fulltext__summary">Document text</summary>
      <div className="doc-fulltext__body">
        <p className="doc-fulltext__note">
          Text extracted from the attached file. Refer to the original
          document for the authoritative version.
        </p>

        {sections.map((section, i) => {
          const anchor = section?.anchor || `section-${i + 1}`;
          return (
            <section
              key={anchor}
              id={anchor}
              className="doc-fulltext__section"
            >
              {/* h2, not h3: the page <h1> is the document title, so an h3
                  here skipped a level (BL-0157, TAIUIT heading-order). The
                  look is carried by .doc-fulltext__heading, not the tag. */}
              <h2 className="doc-fulltext__heading">
                {section?.label || `Section ${i + 1}`}
              </h2>
              {/* Pre-wrap, not a paragraph splitter: the extracted text keeps
                  the document's own line structure, and reflowing it silently
                  merges table rows and numbered clauses into prose. */}
              <div className="doc-fulltext__text">{section?.text || ''}</div>
            </section>
          );
        })}
      </div>
    </details>
  );
};

export default FullTextSection;
